import { useEffect, useState, type CSSProperties } from 'react';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import {
  createApiKey,
  createdWhen,
  lastUsedWhen,
  listApiKeys,
  maskedKey,
  revokeApiKey,
  type ApiKey,
  type StepUpAnswer,
} from '../net/apiKeys';
import { ApiError } from '../net/client';
import { countdown, useOtpStep } from '../session/useOtpStep';
import { useSession, useSessionState } from '../session/useSession';
import { OtpCells, RefusalWords } from './SignIn';
import { chip, colors, fonts, radius, shell, type, verdict } from '../theme';

/**
 * G10, `design/PortalApiKeys.dc.html`.
 *
 * The keys, once, newest first, revoked ones included. New key asks the
 * browser for a name, then `POST`s, and the answer's secret is drawn in the
 * navy banner in full. The secret lives in this component's state and
 * nowhere else: leaving the page, reloading or making another key loses it,
 * and the list never carries it. Revoke asks the browser to confirm, then
 * `DELETE`s. Both are stepped up: the API mails a code first, and the panel
 * above the table, which no board draws, asks for it with sign-in's six
 * cells and spends it on the same request repeated, D41. A refusal is the
 * form board's rejection box above the table.
 * Every route is admin only, so a member is told so and nothing is
 * requested, as Team does. D16.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; keys: ApiKey[] };

/** The one board that draws it: the secret's ink on the navy banner. */
const secretInk = '#FFD9C7';

const columns = '1.4fr 1.2fr 1fr 1fr 0.9fr 0.8fr';

/** What a mailed code is waiting to be spent on. */
type Step =
  | { kind: 'create'; name: string; challengeId: string }
  | { kind: 'revoke'; key: ApiKey; challengeId: string };

/** A code the API refused: typed again in the panel, not the panel closed. */
const codeRefused = new Set(['invalid_code', 'invalid_challenge']);

export function ApiKeys() {
  const state = useSessionState();
  const isAdmin = state.status === 'signed_in' && state.user.role === 'org_admin';
  const email = state.status === 'signed_in' ? state.user.email : '';
  const session = useSession();
  const { api } = session;
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [step, setStep] = useState<Step | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    const controller = new AbortController();
    const { signal } = controller;
    listApiKeys(api, signal).then(
      (keys) => setLoad({ status: 'loaded', keys }),
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api, isAdmin]);

  const forbidden =
    !isAdmin ||
    (load.status === 'failed' && load.error instanceof ApiError && load.error.kind === 'forbidden');
  if (forbidden) {
    return (
      <>
        <PageHeader title="API keys" />
        <Card
          title="API keys are for admins"
          body="Only organization admins can create and revoke API keys."
        />
      </>
    );
  }

  const created = (apiKey: ApiKey, made: string) => {
    setLoad((current) =>
      current.status === 'loaded' ? { ...current, keys: [apiKey, ...current.keys] } : current,
    );
    setSecret(made);
  };

  const revoked = (key: ApiKey) => {
    const revokedAt = new Date().toISOString();
    setLoad((current) =>
      current.status === 'loaded'
        ? {
            ...current,
            keys: current.keys.map((k) => (k.id === key.id ? { ...k, revokedAt } : k)),
          }
        : current,
    );
  };

  const create = async () => {
    const name = window.prompt('Name this key')?.trim();
    if (!name) return;
    setBusy(true);
    setRefusal(null);
    try {
      const made = await createApiKey(api, name);
      if (made.kind === 'challenge')
        setStep({ kind: 'create', name, challengeId: made.challengeId });
      else created(made.apiKey, made.secret);
    } catch (error) {
      setRefusal(failureText(error));
    }
    setBusy(false);
  };

  const copy = async (text: string) => {
    setRefusal(null);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setRefusal('The browser refused the clipboard. Select the key and copy it by hand.');
    }
  };

  const revoke = async (key: ApiKey) => {
    if (!window.confirm(`Revoke ${key.name}? It stops working immediately.`)) return;
    setBusy(true);
    setRefusal(null);
    try {
      const done = await revokeApiKey(api, key.id);
      if (done.kind === 'challenge')
        setStep({ kind: 'revoke', key, challengeId: done.challengeId });
      else revoked(key);
    } catch (error) {
      setRefusal(failureText(error));
    }
    setBusy(false);
  };

  /** The panel's code, spent on the request the challenge stopped. */
  const confirm = async (current: Step, code: string) => {
    const answer: StepUpAnswer = { challengeId: current.challengeId, code };
    try {
      if (current.kind === 'create') {
        const made = await createApiKey(api, current.name, answer);
        if (made.kind === 'challenge') {
          setStep({ ...current, challengeId: made.challengeId });
          return;
        }
        created(made.apiKey, made.secret);
      } else {
        const done = await revokeApiKey(api, current.key.id, answer);
        if (done.kind === 'challenge') {
          setStep({ ...current, challengeId: done.challengeId });
          return;
        }
        revoked(current.key);
      }
      setStep(null);
    } catch (error) {
      if (error instanceof ApiError && codeRefused.has(error.code)) throw error;
      setStep(null);
      setRefusal(failureText(error));
    }
  };

  const locked = busy || step !== null;
  const now = new Date();
  const keys = load.status === 'loaded' ? load.keys : [];

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading your API keys</Waiting> : null}
      <PageHeader
        title="API keys"
        subtitle="Machine credentials for your production line, separate from team sign-ins"
        action={
          <button
            className="vp-fill"
            type="button"
            disabled={locked || load.status !== 'loaded'}
            onClick={() => void create()}
            style={actionButton}
          >
            New key
          </button>
        }
      />
      {load.status === 'failed' ? (
        <Card title="The API keys could not be loaded" body={failureText(load.error)} />
      ) : (
        <>
          {secret !== null ? (
            <section aria-label="Your new key" style={banner}>
              <div
                style={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <h2 style={{ ...type.cardTitle, color: colors.white, margin: 0 }}>
                  Your new key: shown only once
                </h2>
                <div data-part="secret" style={secretText}>
                  {secret}
                </div>
                <div style={{ ...type.caption, color: colors.mutedOnNavy }}>
                  Store it in your line controller now. If it's lost, revoke it and create a new
                  one.
                </div>
              </div>
              <button
                className="vp-fill"
                type="button"
                onClick={() => void copy(secret)}
                style={{ ...actionButton, flexShrink: 0 }}
              >
                Copy key
              </button>
            </section>
          ) : null}
          {step !== null ? (
            <StepUpPanel
              key={step.challengeId}
              title={
                step.kind === 'create'
                  ? `Confirm creating ${step.name}`
                  : `Confirm revoking ${step.key.name}`
              }
              email={email}
              submit={(code) => confirm(step, code)}
              resend={() => session.resendOtp(step.challengeId)}
              onCancel={() => setStep(null)}
            />
          ) : null}
          {refusal !== null ? <Rejection>{refusal}</Rejection> : null}
          <div
            role="table"
            aria-label="API keys"
            aria-busy={load.status === 'loading'}
            style={panel}
          >
            <div role="row" style={{ ...grid, borderBottom: `1px solid ${colors.line}` }}>
              {['Name', 'Key', 'Created', 'Last used', 'Status'].map((h) => (
                <div
                  key={h}
                  role="columnheader"
                  style={{ ...type.tableHeader, color: colors.mutedInk }}
                >
                  {h}
                </div>
              ))}
              <div role="columnheader" aria-label="Action" />
            </div>
            {keys.map((key, i) => {
              const revoked = key.revokedAt !== undefined;
              const tone = revoked ? chip.muted : verdict.genuine;
              return (
                <div key={key.id}>
                  {i > 0 ? <div style={{ height: 1, background: colors.line }} /> : null}
                  <div role="row" style={{ ...grid, alignItems: 'center' }}>
                    <div role="cell" style={{ ...type.bodyStrong, color: colors.navy }}>
                      {key.name}
                    </div>
                    <div
                      role="cell"
                      style={{ fontFamily: fonts.mono, fontSize: '13px', color: colors.mutedInk }}
                    >
                      {maskedKey(key)}
                    </div>
                    <div role="cell" style={{ ...type.body, color: colors.mutedInk }}>
                      {createdWhen(key.createdAt, now)}
                    </div>
                    <div role="cell" style={{ ...type.body, color: colors.mutedInk }}>
                      {lastUsedWhen(key.lastUsedAt, now)}
                    </div>
                    <div role="cell">
                      <span style={chipStyle(tone)}>{revoked ? 'Revoked' : 'Active'}</span>
                    </div>
                    <div role="cell">
                      {revoked ? null : (
                        <button
                          className="vp-link"
                          type="button"
                          disabled={locked}
                          aria-label={`Revoke ${key.name}`}
                          onClick={() => void revoke(key)}
                          style={linkButton}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ ...type.meta, color: colors.mutedInk, margin: 0 }}>
            Keys authenticate the machine API only. They can never sign in to this portal. Revoking
            takes effect immediately.
          </p>
        </>
      )}
    </>
  );
}

/**
 * The step-up's code, in the shell rather than the sign-in frame. No board
 * draws it: the user approved this panel and its words under X9, D41. The
 * cells, the refusal and the resend cooldown are sign-in's own.
 */
function StepUpPanel({
  title,
  email,
  submit,
  resend: send,
  onCancel,
}: {
  title: string;
  email: string;
  submit: (code: string) => Promise<void>;
  resend: () => Promise<void>;
  onCancel: () => void;
}) {
  const { code, setCode, verifying, refusal, secondsLeft, canVerify, verify, resend } = useOtpStep(
    submit,
    send,
  );
  return (
    <section aria-label={title} style={{ ...panel, padding: '20px 24px' }}>
      <form
        noValidate
        onSubmit={verify}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h2 style={{ ...type.cardTitle, color: colors.navy, margin: 0 }}>{title}</h2>
          <p style={{ ...type.body, color: colors.mutedInk, margin: 0 }}>
            We sent a 6-character code to{' '}
            <span style={{ ...type.bodyStrong, color: colors.navy }}>{email}</span>
          </p>
        </div>
        <div style={{ width: cellsWidth }}>
          <OtpCells code={code} onCode={setCode} disabled={verifying} />
        </div>
        {refusal !== null ? (
          <Rejection>
            <RefusalWords refusal={refusal} />
          </Rejection>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <button
            className="vp-fill"
            type="submit"
            disabled={!canVerify}
            aria-busy={verifying}
            style={{
              ...actionButton,
              // Sign-in's disabled look, primary() in signInForm.ts.
              background: canVerify ? colors.brand : colors.disabledBg,
              color: canVerify ? colors.white : colors.disabledFg,
              cursor: canVerify ? 'pointer' : 'default',
            }}
          >
            Confirm
          </button>
          <button
            className="vp-link"
            type="button"
            onClick={onCancel}
            disabled={verifying}
            style={{ ...linkButton, color: colors.mutedInk }}
          >
            Cancel
          </button>
          <div style={{ ...type.meta, color: colors.mutedInk, marginLeft: 'auto' }}>
            Didn&apos;t get it?{' '}
            <button
              className="vp-link"
              type="button"
              onClick={resend}
              aria-disabled={secondsLeft > 0}
              aria-label={
                secondsLeft > 0
                  ? `Resend, available in ${countdown(secondsLeft)}`
                  : 'Resend the code'
              }
              style={{ ...linkButton, color: colors.brand }}
            >
              Resend
            </button>
            {secondsLeft > 0 ? ` available in ${countdown(secondsLeft)}` : null}
          </div>
        </div>
      </form>
    </section>
  );
}

/** Sign-in's six 46px cells and their five 8px gaps. */
const cellsWidth = 6 * 46 + 5 * 8;

/** The API's field message when it names one, else its own message. */
function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof ApiError && error.details !== undefined) {
    const fields = Object.values(error.details);
    if (fields.length > 0) return fields.join(' ');
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const panel: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: columns,
  gap: 14,
  padding: '14px 20px',
};

const banner: CSSProperties = {
  borderRadius: radius.card,
  background: colors.navy,
  padding: '20px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: 20,
};

/** Drawn shortened; shown whole, so a refused clipboard still leaves a way to copy it. D16. */
const secretText: CSSProperties = {
  fontFamily: fonts.mono,
  fontSize: '14px',
  color: secretInk,
  letterSpacing: '0.02em',
  overflowWrap: 'anywhere',
  userSelect: 'all',
};

const chipStyle = (tone: { bg: string; fg: string }): CSSProperties => ({
  display: 'inline-flex',
  padding: '4px 12px',
  borderRadius: radius.chip,
  background: tone.bg,
  color: tone.fg,
  ...type.captionBold,
});

const linkButton: CSSProperties = {
  ...type.metaBold,
  color: verdict.unknown.fg,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const actionButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  background: colors.brand,
  color: colors.white,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};
